import {
  BellRing,
  CircleUserRound,
  Clock3,
  Crosshair,
  HeartPulse,
  ListRestart,
  LoaderCircle,
  Map as MapIcon,
  MapPinPlus,
  MapPinned,
  Navigation2,
  ShieldAlert,
  Siren,
  Sparkles,
  X,
} from "lucide-react";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { AuthScreen } from "./components/AuthScreen";
import { MemberRail } from "./components/MemberRail";
import {
  AddPlaceSheet,
  MemberSheet,
  PlaceDetailSheet,
  PlacesView,
  ProfileView,
  Sheet,
  TimelineView,
} from "./components/Sheets";
import { api, ApiError } from "./lib/api";
import type {
  Alert,
  Bootstrap,
  Member,
  Place,
  RealtimeEvent,
  TrailPoint,
} from "./types";

type Tab = "map" | "timeline" | "places" | "profile";
type GpsState = "starting" | "live" | "weak" | "denied" | "unavailable";

const MapCanvas = lazy(() =>
  import("./components/MapCanvas").then((module) => ({
    default: module.MapCanvas,
  })),
);

function Dashboard({ onLoggedOut }: { onLoggedOut: () => void }) {
  const [data, setData] = useState<Bootstrap | null>(null);
  const [loadingError, setLoadingError] = useState("");
  const [tab, setTab] = useState<Tab>("map");
  const [gpsState, setGpsState] = useState<GpsState>("starting");
  const [lastPosition, setLastPosition] = useState<{
    latitude: number;
    longitude: number;
    accuracy: number;
  } | null>(null);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [addingPlace, setAddingPlace] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [trail, setTrail] = useState<TrailPoint[]>([]);
  const [focusRequest, setFocusRequest] = useState<{
    id: string;
    nonce: number;
  } | null>(null);
  const [sosOpen, setSosOpen] = useState(false);
  const [sendingSos, setSendingSos] = useState(false);
  const [toast, setToast] = useState("");
  const [locationNonce, setLocationNonce] = useState(0);
  const lastSentAt = useRef(0);

  const load = useCallback(async () => {
    try {
      const result = await api.bootstrap();
      setData(result);
      setLoadingError("");
    } catch (error) {
      setLoadingError(error instanceof Error ? error.message : "โหลดข้อมูลไม่สำเร็จ");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 3600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!data) return;
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(`${protocol}//${window.location.host}/realtime`);
    socket.onmessage = (message) => {
      const event = JSON.parse(message.data) as RealtimeEvent;
      if (event.type === "location.updated") {
        setData((current) =>
          current
            ? {
                ...current,
                members: current.members.map((member) =>
                  member.id === event.location.userId
                    ? {
                        ...member,
                        latitude: event.location.latitude,
                        longitude: event.location.longitude,
                        accuracy: event.location.accuracy,
                        heading: event.location.heading,
                        speed: event.location.speed,
                        battery: event.location.battery,
                        capturedAt: event.location.capturedAt,
                      }
                    : member,
                ),
              }
            : current,
        );
      }
      if (event.type === "sharing.updated") {
        setData((current) =>
          current
            ? {
                ...current,
                members: current.members.map((member) =>
                  member.id === event.userId
                    ? { ...member, shareLocation: event.enabled }
                    : member,
                ),
              }
            : current,
        );
      }
      if (event.type === "place.created") {
        setData((current) =>
          current &&
          !current.places.some((place) => place.id === event.place.id)
            ? { ...current, places: [event.place, ...current.places] }
            : current,
        );
      }
      if (event.type === "visit.started") {
        setToast(`${event.user.name} ถึง ${event.visit.name} แล้ว ${event.visit.emoji}`);
        void load();
      }
      if (event.type === "alert.sos") {
        setData((current) =>
          current
            ? { ...current, alerts: [event.alert, ...current.alerts] }
            : current,
        );
        setToast(`SOS จาก ${event.alert.userName}`);
      }
      if (event.type === "alert.resolved") {
        setData((current) =>
          current
            ? {
                ...current,
                alerts: current.alerts.map((alert) =>
                  alert.id === event.id
                    ? { ...alert, resolvedAt: event.resolvedAt }
                    : alert,
                ),
              }
            : current,
        );
      }
    };
    return () => socket.close();
  }, [data?.space.id, load]);

  const sharing = data?.membership.share_location ?? false;

  useEffect(() => {
    if (!data || !sharing) {
      if (data && !sharing) setGpsState("unavailable");
      return;
    }
    if (!navigator.geolocation) {
      setGpsState("unavailable");
      return;
    }
    setGpsState("starting");
    let cancelled = false;
    let battery: number | null = null;
    const batteryApi = navigator as Navigator & {
      getBattery?: () => Promise<{ level: number }>;
    };
    void batteryApi.getBattery?.().then((result) => {
      battery = result.level;
    });

    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        if (cancelled) return;
        const current = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
        setLastPosition(current);
        setGpsState(position.coords.accuracy <= 80 ? "live" : "weak");
        setData((state) =>
          state
            ? {
                ...state,
                members: state.members.map((member) =>
                  member.id === state.user.id
                    ? {
                        ...member,
                        ...current,
                        heading: position.coords.heading,
                        speed: position.coords.speed,
                        battery,
                        capturedAt: new Date(position.timestamp).toISOString(),
                      }
                    : member,
                ),
              }
            : state,
        );
        if (
          position.coords.accuracy > 200 ||
          Date.now() - lastSentAt.current < 5_000
        ) {
          return;
        }
        lastSentAt.current = Date.now();
        try {
          const result = await api.updateLocation({
            ...current,
            heading: position.coords.heading,
            speed: position.coords.speed,
            battery,
            capturedAt: new Date(position.timestamp).toISOString(),
          });
          if (result.newVisits.length) {
            const visit = result.newVisits[0];
            setToast(`มาถึง ${visit.name} แล้ว ${visit.emoji}`);
            void load();
          }
        } catch (error) {
          if (error instanceof ApiError && error.code === "LOW_ACCURACY") {
            setGpsState("weak");
          }
        }
      },
      (error) => {
        if (cancelled) return;
        setGpsState(error.code === error.PERMISSION_DENIED ? "denied" : "unavailable");
      },
      {
        enableHighAccuracy: true,
        maximumAge: 3_000,
        timeout: 20_000,
      },
    );

    return () => {
      cancelled = true;
      navigator.geolocation.clearWatch(watchId);
    };
  }, [data?.user.id, sharing, locationNonce, load]);

  async function loadTrail(memberId: string, hours = 24) {
    try {
      const result = await api.history(memberId, hours);
      setTrail(result.points);
    } catch (error) {
      setToast(error instanceof Error ? error.message : "โหลดเส้นทางไม่สำเร็จ");
    }
  }

  function selectMember(member: Member) {
    setSelectedMember(member);
    setFocusRequest({ id: member.id, nonce: Date.now() });
    void loadTrail(member.id);
  }

  function addAtCurrentPosition() {
    if (lastPosition) {
      setAddingPlace(lastPosition);
      return;
    }
    const me = data?.members.find((member) => member.id === data.user.id);
    if (me?.latitude != null && me.longitude != null) {
      setAddingPlace({ latitude: me.latitude, longitude: me.longitude });
      return;
    }
    setToast("เปิดตำแหน่งก่อนเพิ่มสถานที่");
    setTab("map");
    setLocationNonce((value) => value + 1);
  }

  async function toggleSharing() {
    if (!data) return;
    const enabled = !data.membership.share_location;
    await api.setSharing(enabled);
    setData({
      ...data,
      membership: { ...data.membership, share_location: enabled },
      members: data.members.map((member) =>
        member.id === data.user.id
          ? { ...member, shareLocation: enabled }
          : member,
      ),
    });
    if (enabled) setLocationNonce((value) => value + 1);
    setToast(enabled ? "เริ่มแชร์ตำแหน่งแล้ว" : "พักการแชร์ตำแหน่งแล้ว");
  }

  async function sendSos() {
    setSendingSos(true);
    try {
      const result = await api.sendSos("ต้องการความช่วยเหลือ กรุณาติดต่อฉัน");
      setData((current) =>
        current
          ? { ...current, alerts: [result.alert, ...current.alerts] }
          : current,
      );
      setSosOpen(false);
      setToast("ส่ง SOS ให้ทุกคนในกลุ่มแล้ว");
      navigator.vibrate?.([100, 50, 100]);
    } catch (error) {
      setToast(error instanceof Error ? error.message : "ส่ง SOS ไม่สำเร็จ");
    } finally {
      setSendingSos(false);
    }
  }

  async function resolveAlert(alert: Alert) {
    const result = await api.resolveAlert(alert.id);
    setData((current) =>
      current
        ? {
            ...current,
            alerts: current.alerts.map((item) =>
              item.id === result.id
                ? { ...item, resolvedAt: result.resolvedAt }
                : item,
            ),
          }
        : current,
    );
  }

  if (!data) {
    return (
      <main className="loading-screen">
        {loadingError ? (
          <>
            <span>🍬</span>
            <h1>ยังเปิดแผนที่ไม่ได้</h1>
            <p>{loadingError}</p>
            <button className="primary-button" onClick={() => void load()}>
              ลองอีกครั้ง
            </button>
          </>
        ) : (
          <>
            <LoaderCircle className="spin" size={38} />
            <p>กำลังเตรียมแผนที่ของคุณ…</p>
          </>
        )}
      </main>
    );
  }

  const unresolvedAlert = data.alerts.find((alert) => !alert.resolvedAt);
  const ownMember = data.members.find((member) => member.id === data.user.id);

  return (
    <main className="app-shell">
      {unresolvedAlert && (
        <div className="sos-banner">
          <span className="sos-banner-icon"><Siren size={20} /></span>
          <span>
            <b>{unresolvedAlert.userName} ส่ง SOS</b>
            <small>{unresolvedAlert.message}</small>
          </span>
          <button onClick={() => void resolveAlert(unresolvedAlert)}>รับทราบ</button>
        </div>
      )}

      <section className={`app-content ${tab === "map" ? "map-tab" : ""}`}>
        {tab === "map" && (
          <div className="map-screen">
            <Suspense
              fallback={
                <div className="map-loading">
                  <LoaderCircle className="spin" size={28} />
                  <span>กำลังเปิดแผนที่…</span>
                </div>
              }
            >
              <MapCanvas
                members={data.members}
                places={data.places}
                selectedMemberId={selectedMember?.id ?? data.user.id}
                trail={trail}
                focusRequest={focusRequest}
                onSelectMember={(memberId) => {
                  const member = data.members.find((item) => item.id === memberId);
                  if (member) selectMember(member);
                }}
                onSelectPlace={setSelectedPlace}
                onLongPress={setAddingPlace}
              />
            </Suspense>

            <header className="map-topbar">
              <div className="map-brand">
                <span className="brand-mark"><MapPinned size={21} /></span>
                <span><b>Candy tracker</b><small>{data.space.name}</small></span>
              </div>
              <div className={`live-status ${gpsState}`}>
                <i />
                <span>
                  {gpsState === "live"
                    ? `สด · ±${Math.round(lastPosition?.accuracy ?? ownMember?.accuracy ?? 0)} ม.`
                    : gpsState === "weak"
                      ? "กำลังหาสัญญาณ"
                      : gpsState === "denied"
                        ? "ปิด GPS"
                        : gpsState === "starting"
                          ? "กำลังเชื่อม"
                          : "พักแชร์"}
                </span>
              </div>
            </header>

            <MemberRail
              members={data.members}
              selectedId={selectedMember?.id ?? data.user.id}
              onSelect={selectMember}
            />

            {gpsState === "denied" && (
              <div className="gps-prompt">
                <span><ShieldAlert size={20} /></span>
                <p><b>อนุญาตตำแหน่งเพื่อเริ่มแชร์</b><small>เปิดสิทธิ์ Location ในการตั้งค่าเบราว์เซอร์</small></p>
                <button onClick={() => setLocationNonce((value) => value + 1)}>ลองใหม่</button>
              </div>
            )}

            <div className="map-actions">
              <button
                type="button"
                className="map-action-button"
                onClick={() => {
                  if (ownMember) {
                    setFocusRequest({ id: ownMember.id, nonce: Date.now() });
                  }
                  setLocationNonce((value) => value + 1);
                }}
                aria-label="กลับไปที่ตำแหน่งฉัน"
              >
                <Crosshair size={21} />
              </button>
              <button
                type="button"
                className="map-action-button"
                onClick={addAtCurrentPosition}
                aria-label="เพิ่มสถานที่ตรงนี้"
              >
                <MapPinPlus size={21} />
              </button>
              <button
                type="button"
                className="map-action-button sos"
                onClick={() => setSosOpen(true)}
                aria-label="ส่ง SOS"
              >
                <HeartPulse size={21} />
              </button>
            </div>

            <div className="map-tip">
              <Sparkles size={14} />
              กดค้างบนแผนที่เพื่อจำสถานที่
            </div>
          </div>
        )}
        {tab === "timeline" && <TimelineView visits={data.visits} />}
        {tab === "places" && (
          <PlacesView
            places={data.places}
            onSelect={setSelectedPlace}
            onAddCurrent={addAtCurrentPosition}
          />
        )}
        {tab === "profile" && (
          <ProfileView
            data={data}
            sharing={sharing}
            onToggleSharing={toggleSharing}
            onRegenerateInvite={async () => {
              const result = await api.regenerateInvite();
              setData({
                ...data,
                space: { ...data.space, inviteCode: result.inviteCode },
              });
              setToast("สร้างรหัสเชิญใหม่แล้ว");
            }}
            onClearHistory={async () => {
              await api.clearHistory();
              setTrail([]);
              setToast("ลบประวัติเส้นทางแล้ว");
            }}
            onLogout={async () => {
              await api.logout();
              onLoggedOut();
            }}
          />
        )}
      </section>

      <nav className="bottom-nav" aria-label="เมนูหลัก">
        {(
          [
            ["map", MapIcon, "แผนที่"],
            ["timeline", ListRestart, "ไปมาแล้ว"],
            ["places", Navigation2, "สถานที่"],
            ["profile", CircleUserRound, "ฉัน"],
          ] as const
        ).map(([value, Icon, label]) => (
          <button
            type="button"
            key={value}
            className={tab === value ? "active" : ""}
            onClick={() => setTab(value)}
          >
            <span><Icon size={22} strokeWidth={tab === value ? 2.6 : 2} /></span>
            <small>{label}</small>
          </button>
        ))}
      </nav>

      {selectedMember && (
        <MemberSheet
          member={
            data.members.find((member) => member.id === selectedMember.id) ??
            selectedMember
          }
          trail={trail}
          onClose={() => setSelectedMember(null)}
          onLoadTrail={(hours) => loadTrail(selectedMember.id, hours)}
          onFocus={() => {
            setFocusRequest({ id: selectedMember.id, nonce: Date.now() });
            setSelectedMember(null);
            setTab("map");
          }}
        />
      )}

      {selectedPlace && (
        <PlaceDetailSheet
          place={
            data.places.find((place) => place.id === selectedPlace.id) ??
            selectedPlace
          }
          onClose={() => setSelectedPlace(null)}
          onCheckIn={async () => {
            await api.checkIn(selectedPlace.id);
            setToast(`เช็กอินที่ ${selectedPlace.name} แล้ว`);
            await load();
          }}
        />
      )}

      {addingPlace && (
        <AddPlaceSheet
          coordinates={addingPlace}
          onClose={() => setAddingPlace(null)}
          onSave={async (input) => {
            const result = await api.createPlace(input);
            setData({
              ...data,
              places: [result.place, ...data.places],
            });
            setToast(`จำ ${result.place.name} ไว้แล้ว`);
          }}
        />
      )}

      {sosOpen && (
        <Sheet title="ส่งสัญญาณ SOS" onClose={() => setSosOpen(false)} className="sos-sheet">
          <div className="sos-confirm">
            <span className="sos-pulse"><Siren size={34} /></span>
            <h3>ต้องการความช่วยเหลือใช่ไหม?</h3>
            <p>ทุกคนในกลุ่มจะเห็นการแจ้งเตือนพร้อมตำแหน่งล่าสุดของคุณทันที</p>
            <button className="danger-button" disabled={sendingSos} onClick={() => void sendSos()}>
              <BellRing size={19} />
              {sendingSos ? "กำลังส่ง…" : "ส่ง SOS ตอนนี้"}
            </button>
            <button className="quiet-button" onClick={() => setSosOpen(false)}>ยังไม่ส่ง</button>
          </div>
        </Sheet>
      )}

      {toast && (
        <div className="toast" role="status">
          <span>🍬</span>{toast}
          <button onClick={() => setToast("")} aria-label="ปิด"><X size={16} /></button>
        </div>
      )}
    </main>
  );
}

export default function App() {
  const [status, setStatus] = useState<"checking" | "guest" | "authenticated">("checking");

  useEffect(() => {
    api
      .me()
      .then(() => setStatus("authenticated"))
      .catch(() => setStatus("guest"));
  }, []);

  if (status === "checking") {
    return (
      <main className="splash-screen">
        <span className="splash-candy"><MapPinned size={34} /></span>
        <h1>Candy tracker</h1>
        <div className="splash-loader"><i /><i /><i /></div>
      </main>
    );
  }

  return status === "guest" ? (
    <AuthScreen onAuthenticated={() => setStatus("authenticated")} />
  ) : (
    <Dashboard onLoggedOut={() => setStatus("guest")} />
  );
}
